/**
 * THE PLACEHOLDER EMAIL HAND-OFF — the one message every surface shows when
 * setting a placeholder's email answers `409 email_in_use` (2026-09-23, P6 of
 * the join-a-second-department feature).
 *
 * Owner, late on 2026-09-23: *"a placeholder can be updated with an email
 * address and then the person can be invited. IF they are already invited the
 * only option is to delete the placeholder"* and *"keep it very simple"*.
 *
 * So: ONE clear sentence, no merge flow, nothing deleted for the user, no name
 * matching. The holder is named ONLY when the server named them —
 * `server/helpers/userAccess.js#describeEmailConflict` adds `holder` to the body
 * only when the caller can already see that person; out of scope it names
 * nobody, and this helper must never try to fill the gap.
 *
 * `onBudget` appends the Manage Budgets route out: there the crew line itself
 * can carry the invite (`crew_member_id`), so the person is linked to the line
 * when they join.
 */
export const EMAIL_IN_USE_CODE = 'email_in_use';

export const EMAIL_IN_USE_HANDOFF =
  'That email already belongs to a CrewForge account. Delete this placeholder and invite that person instead.';

export const EMAIL_IN_USE_BUDGET_SUFFIX =
  ' — or use Send Invite on the crew line to invite them to it';

/**
 * `detailsSaved` (Manage Budgets only): the crew row's own writes have ALREADY
 * succeeded and only the email PUT was refused, so the message must say the
 * other changes stand (review, fix round 1, 2026-09-23). Never pass it from a
 * path where nothing was saved — the ADD path's 409 means the add failed.
 *
 * @param {object|undefined} body — the parsed 409 body, if any
 * @param {{ onBudget?: boolean, detailsSaved?: boolean }} [opts]
 * @returns {string}
 */
export function emailInUseHandoffMessage(body, { onBudget = false, detailsSaved = false } = {}) {
  const holderName = body && body.holder && typeof body.holder.display_name === 'string'
    ? body.holder.display_name.trim()
    : '';
  if (onBudget && detailsSaved) {
    const who = holderName ? ` (${holderName})` : '';
    return `Crew member details saved. That email already belongs to a CrewForge account${who} — `
      + 'delete this placeholder and invite that person instead, or use Send Invite on the crew line.';
  }
  const lead = holderName
    ? `That email already belongs to a CrewForge account (${holderName}).`
    : 'That email already belongs to a CrewForge account.';
  const rest = ' Delete this placeholder and invite that person instead';
  return onBudget
    ? `${lead}${rest}${EMAIL_IN_USE_BUDGET_SUFFIX}.`
    : `${lead}${rest}.`;
}

/** True for an Error or a response body carrying the email_in_use code. */
export function isEmailInUse(errOrBody) {
  return !!errOrBody && errOrBody.code === EMAIL_IN_USE_CODE;
}
